import { useCallback, useRef, useState } from 'react';

import { AxiosError } from 'axios';
import { addSeconds } from 'date-fns';

import {
    MdsCallKeys,
    RBBottomTabMenuItem,
    RBChannel,
    RBParsedRail,
} from 'store/railsBuilder/reducer.types';

import { ContentType } from 'services/data-manager/types';

import { CollectionItemsList } from 'screens/Collection/types';
import { OnSetStripeLoadingStatus } from 'screens/Modular/types';
import { SearchOptions, SearchType } from 'screens/Search/types';

import useLoadGenericRail, { INITIAL_PAGE } from './useLoadGenericRail';
import useLoadNowNext from './useLoadNowNext';
import useLoadRecordings from './useLoadRecordings';
import useLoadSearch, { SEARCH_LIMIT } from './useLoadSearch';

type Params = {
    rail: RBParsedRail | undefined;
    templateId: RBBottomTabMenuItem['id'] | undefined;
    channels: RBChannel[];
    searchQuery?: string;
    searchType?: SearchType;
    searchOptions?: SearchOptions;
    limit?: number;
    isCollection?: boolean;
    onSetStripeLoadingStatus?: OnSetStripeLoadingStatus;
};

const DEFAULT_COLLECTION_LIMIT = 50;

function useCollectionDataManager({
    isCollection = false,
    templateId,
    rail,
    channels,
    searchType,
    searchQuery,
    searchOptions,
    limit = DEFAULT_COLLECTION_LIMIT,
    onSetStripeLoadingStatus,
}: Params) {
    const [isFetching, setFetching] = useState(true);
    const [hasNext, setHasNext] = useState(false);
    const [data, setData] = useState<CollectionItemsList>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [page, setPage] = useState(INITIAL_PAGE);
    const [nextUpdateTime, setNextUpdateTime] = useState<number | undefined>();
    const [errorStatus, setErrorStatus] = useState<number | undefined>();
    const [isErrorOnLoadingNextPage, setIsErrorOnLoadingNextPage] =
        useState<boolean>(false);

    const lastRequestedPage = useRef(page);

    const onSetNextUpdateTime = useCallback((timestamp: number) => {
        // Add 5 seconds delay to allow backend to refresh the cache.
        setNextUpdateTime(addSeconds(timestamp, 5).getTime());
    }, []);

    const { onLoadRecordings, onLoadRecordingsNextPage } = useLoadRecordings({
        rail,
        isCollection,
        setTotalItems,
        setPage,
        setHasNext,
        setData,
        onSetStripeLoadingStatus,
    });
    const { onLoadNowNext, onLoadKidsNowNext } = useLoadNowNext({
        templateId,
        rail,
        channels,
        isCollection,
        limit,
        setTotalItems,
        setPage,
        setHasNext,
        setData,
        onSetNextUpdateTime,
        onSetStripeLoadingStatus,
    });
    const onLoadGenericRail = useLoadGenericRail({
        rail,
        templateId,
        limit,
        setHasNext,
        setData,
        setPage,
        setTotalItems,
        onSetStripeLoadingStatus,
    });
    const { onLoadSearch, onLoadSearchNextPage } = useLoadSearch({
        searchType,
        searchQuery,
        searchOptions,
        setData,
        setPage,
        setTotalItems,
    });

    const handleDiscardErrorOnLoadingNextPage = useCallback(() => {
        setIsErrorOnLoadingNextPage(false);
        setErrorStatus(undefined);
    }, []);

    const handleLoadNextPage = useCallback(async () => {
        const nextPage = page + 1;

        if (data?.length < totalItems && nextPage > lastRequestedPage.current) {
            try {
                if (searchQuery && searchType && totalItems > SEARCH_LIMIT) {
                    await onLoadSearchNextPage(nextPage);
                    lastRequestedPage.current = nextPage;
                } else if (
                    rail?.properties.ContentType === ContentType.NPVR &&
                    rail.properties.MDS_Call === MdsCallKeys.RECORDINGS &&
                    rail.properties.RailType
                ) {
                    await onLoadRecordingsNextPage(nextPage);
                    lastRequestedPage.current = nextPage;
                } else if (rail && templateId) {
                    await onLoadGenericRail(nextPage);
                    lastRequestedPage.current = nextPage;
                }
            } catch (error) {
                lastRequestedPage.current = nextPage - 1;
                setIsErrorOnLoadingNextPage(true);
                setErrorStatus((error as AxiosError).response?.status);
            }
        }
    }, [
        templateId,
        page,
        searchQuery,
        searchType,
        totalItems,
        data?.length,
        rail,
        onLoadSearchNextPage,
        onLoadRecordingsNextPage,
        onLoadGenericRail,
    ]);

    const handleGetCollectionData = useCallback(
        async (silently: boolean = false) => {
            lastRequestedPage.current = INITIAL_PAGE;

            if (searchQuery && searchType) {
                await onLoadSearch();
            } else if (
                rail?.properties.ContentType === ContentType.NPVR &&
                rail.properties.MDS_Call === MdsCallKeys.RECORDINGS &&
                rail.properties.RailType
            ) {
                await onLoadRecordings(silently);
            } else if (
                rail?.properties.ContentType === ContentType.NowNext &&
                rail.properties.MDS_Call === MdsCallKeys.NOW_NEXT
            ) {
                await onLoadNowNext(silently);
            } else if (
                rail?.properties.ContentType === ContentType.NowNext &&
                !rail.properties.MDS_Call
            ) {
                await onLoadKidsNowNext(silently);
            } else if (rail && templateId) {
                await onLoadGenericRail(INITIAL_PAGE);
            }

            setFetching(false);
        },
        [
            searchQuery,
            searchType,
            rail,
            templateId,
            onLoadRecordings,
            onLoadNowNext,
            onLoadKidsNowNext,
            onLoadGenericRail,
            onLoadSearch,
        ],
    );

    return {
        data,
        hasNext,
        totalItems,
        nextUpdateTime,
        errorStatus,
        isFetching,
        isCollection,
        isErrorOnLoadingNextPage,
        onDiscardErrorOnLoadingNextPage: handleDiscardErrorOnLoadingNextPage,
        onGetCollectionData: handleGetCollectionData,
        onLoadNextPage: handleLoadNextPage,
        setFetching,
    };
}

export default useCollectionDataManager;
